import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { templatesApi, AvailableTemplate } from '../../api';
import { BRAND } from '../../brand';
import './Onboarding.css';
import './TemplatePicker.css';

interface Props {
  /** Called once a template has been successfully assigned to the user.
   *  Parent should refetch templates so the rest of the dashboard can render. */
  onAssigned: (templateId: number) => void;
}

/** Russian-language labels for known recipient values. UI labels for other
 *  template languages reuse these — recipient values are stored as Russian
 *  slugs in the DB (мама, парень, девушка, …). */
const RECIPIENT_LABEL: Record<string, string> = {
  'мама': 'Маме',
  'папа': 'Папе',
  'родители': 'Родителям',
  'жена': 'Жене',
  'муж': 'Мужу',
  'девушка': 'Девушке',
  'парень': 'Парню',
  'дочь': 'Дочке',
  'сын': 'Сыну',
  'дети': 'Детям',
  'сестра': 'Сестре',
  'брат': 'Брату',
  'братья': 'Братьям',
  'бабушка': 'Бабушке',
  'дедушка': 'Дедушке',
  'бабушка_дедушка': 'Бабушке и дедушке',
  'друг': 'Другу',
  'подруга': 'Подруге',
  'друзья': 'Друзьям',
  'подруги': 'Подругам',
  'тётя': 'Тёте',
  'дядя': 'Дяде',
  'дядя_тётя': 'Дяде и тёте',
  'племянница': 'Племяннице',
  'крестница': 'Крестнице',
  'внуки': 'Внукам',
  'себе': 'Себе',
  'коллега': 'Коллеге',
  'руководитель': 'Руководителю',
  'учитель': 'Учителю',
  'свекровь': 'Свекрови',
  'хирург': 'Хирургу',
  'близняшка': 'Близняшке',
  'бывшая': 'Бывшей',
  'бывший': 'Бывшему',
};

/** Display labels for the four supported template languages. */
const LANGUAGE_LABEL: Record<string, string> = {
  ru: 'Русский',
  en: 'Английский',
  kz: 'Казахский',
  uk: 'Украинский',
};

/** Recipients pinned to the front of the picker. */
const RECIPIENT_PRIORITY = ['парень', 'девушка', 'муж', 'жена'];

const labelForRecipient = (r: string) =>
  RECIPIENT_LABEL[r] || r.charAt(0).toUpperCase() + r.slice(1);

const labelForLanguage = (lang: string) =>
  LANGUAGE_LABEL[lang] || lang.toUpperCase();

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  id: string;
  label: string;
  placeholder: string;
  options: DropdownOption[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

/** Custom dropdown (mg-tpl__menu /
 *  mg-tpl__item) and the surrounding mg-tpl field rule (label + bottom line). */
function Dropdown({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div className="mg-tpl__field" ref={rootRef}>
      <label className="mg-tpl__label" htmlFor={id}>{label}</label>
      <div className="mg-tpl__dropdown">
        <button
          id={id}
          type="button"
          className="mg-tpl__trigger"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={current ? '' : 'mg-tpl__placeholder'}>
            {current?.label ?? placeholder}
          </span>
          <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden>
            <path fill="currentColor" d="M0 0l5 6 5-6z" />
          </svg>
        </button>
        {open && options.length > 0 && (
          <ul className="mg-tpl__menu" role="listbox">
            {options.map((o) => {
              const active = o.value === value;
              return (
                <li key={o.value} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={`mg-tpl__item ${active ? 'mg-tpl__item--active' : ''}`}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    {o.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export const TemplatePicker: React.FC<Props> = ({ onAssigned }) => {
  const { t } = useTranslation('onboarding');
  const [available, setAvailable] = useState<AvailableTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [recipient, setRecipient] = useState<string>('');
  const [language, setLanguage] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    templatesApi
      .getAvailable()
      .then(({ data }) => {
        if (!alive) return;
        setAvailable(data);
      })
      .catch(() => {
        if (alive) setError(t('tpl_load_error') as string);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [t]);

  // Unique recipients across all available templates. Pinned values
  // (boyfriend/girlfriend/husband/wife) come first.
  const recipients = useMemo(() => {
    const set = new Set<string>();
    available.forEach((a) => set.add(a.recipient));
    const all = Array.from(set);
    const pinned = RECIPIENT_PRIORITY.filter((r) => set.has(r));
    const rest = all.filter((r) => !RECIPIENT_PRIORITY.includes(r));
    return [...pinned, ...rest];
  }, [available]);

  // Languages available for the currently selected recipient.
  const languagesForRecipient = useMemo(() => {
    if (!recipient) return [] as string[];
    const set = new Set<string>();
    available
      .filter((a) => a.recipient === recipient)
      .forEach((a) => set.add(a.language));
    const order = ['ru', 'en', 'kz', 'uk'];
    return Array.from(set).sort(
      (a, b) => order.indexOf(a) - order.indexOf(b),
    );
  }, [available, recipient]);

  // If the current language isn't valid for the new recipient — clear it.
  useEffect(() => {
    if (language && !languagesForRecipient.includes(language)) {
      setLanguage('');
    }
  }, [language, languagesForRecipient]);

  const canSubmit = !!recipient && !!language && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await templatesApi.assign(recipient, language);
      onAssigned(data.templateId);
    } catch (e: any) {
      setError(e?.response?.data?.error || (t('tpl_assign_error') as string));
      setSubmitting(false);
    }
  };

  return (
    <div className="mg-onb">
      <div className="mg-onb__bar">
        <span className="mg-onb__bar-logo">{BRAND.wordmark}</span>
      </div>

      <div className="mg-onb__stage">
        <div className="mg-onb__step mg-tpl">
          <div className="mg-onb__kicker">{t('tpl_kicker')}</div>
          <h1 className="mg-onb__title">{t('tpl_title')}</h1>

          <Dropdown
            id="tpl-recipient"
            label={t('tpl_recipient_label')}
            placeholder={
              loading
                ? (t('tpl_loading') as string)
                : (t('tpl_recipient_placeholder') as string)
            }
            disabled={loading}
            value={recipient}
            onChange={setRecipient}
            options={recipients.map((r) => ({
              value: r,
              label: labelForRecipient(r),
            }))}
          />

          <Dropdown
            id="tpl-language"
            label={t('tpl_language_label')}
            placeholder={
              recipient
                ? (t('tpl_language_placeholder') as string)
                : (t('tpl_language_pick_recipient') as string)
            }
            disabled={!recipient || languagesForRecipient.length === 0}
            value={language}
            onChange={setLanguage}
            options={languagesForRecipient.map((l) => ({
              value: l,
              label: labelForLanguage(l),
            }))}
          />

          {error && <p className="mg-onb__note mg-onb__error">{error}</p>}

          <div className="mg-onb__actions">
            <button
              className="mg-btn"
              onClick={handleSubmit}
              disabled={!canSubmit}
              type="button"
            >
              {submitting ? t('tpl_submitting') : t('tpl_continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
